import { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons/faExternalLinkAlt'
import { faPenNib } from '@fortawesome/free-solid-svg-icons/faPenNib'
import { faVideo } from '@fortawesome/free-solid-svg-icons/faVideo'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { Theme } from '@mui/material/styles'
import Typography from '@mui/material/Typography'
import { SxProps } from '@mui/system'
import { thirdPartyCollectSources } from '@tm/client-app/src/survey/editor-v3/Canvas/Thanks/ThirdPartyCollect/sources'
import useI18N from '@tm/client-form/src/hooks/useI18n'
import { useReplaceSegmentParameters } from '@tm/client-form/src/hooks/useReplaceSegmentParameters'
import { useSurveyProps } from '@tm/client-form/src/hooks/useSurveyProps'
import { ReviewConnection } from '@tm/types/db'
import { ThirdPartyCollectSource } from '@tm/types/survey/third-party-collect'
import { FontAwesomeSvgIcon as Icon } from '@tm/ui/composite/FontAwesomeSvgIcon'
import { useCallback } from 'react'
import { useCampaign } from '../../../../hooks/useCampaign'
import { isSourceInMainSegment } from '../../../../misc/isSourceInMainSegment'
import { CopyText } from '../../../field/CopyText'
import { UsePlatformFields } from './usePlatformFields'
import { usePlatformRedirect } from './usePlatformRedirect'
import { ReviewFields } from './useReviewFields'

const getSourceName = (source: ReviewConnection['source']) => {
  switch (source) {
    case 'facebook':
    case 'facebook-v2':
      return 'Facebook'
    case 'google':
    case 'googlemaps':
      return 'Google'
    case 'g2':
      return 'G2'
    case 'capterra':
      return 'Capterra'
    case 'tripadvisor':
      return 'Tripadvisor'
    case 'yelp':
      return 'Yelp'
    case 'googleplay':
      return 'Google Play'
    case 'booking':
      return 'Booking.com'
    case 'trustedshops':
      return 'Trustedshops'
    case 'airbnb':
      return 'Airbnb'

    default:
      return source
  }
}

const sourceLogos = new Map(thirdPartyCollectSources.map(({ name, logo }) => [name, logo]))
sourceLogos.set('Trustmary', 'https://d2nce6johdc51d.cloudfront.net/source/trustmary.svg')

type PlatformViewProps = Partial<UsePlatformFields> & {
  reviewFields?: ReviewFields
  noRedirect?: boolean
  copyText?: string | null
}

type PlatformButtonProps = {
  text: string
  url?: string
  onClick?: () => void
  iconSrc?: string
  faIcon?: IconDefinition
}

function PlatformButton({ text, url, onClick, iconSrc, faIcon }: PlatformButtonProps) {
  const { primaryColor } = useCampaign()
  const redirectUrl = useReplaceSegmentParameters(url || '')

  const buttonSx: SxProps<Theme> = {
    bgcolor: 'background.paper',
    borderRadius: 1,
    boxShadow: theme => theme.customShadows.card,
    color: 'text.primary',
    px: 2,
    py: 3,
    flexBasis: {
      xs: '100%',
      md: 'max-content',
    },
  }

  const buttonInner = (
    <Stack gap={2} sx={{ alignItems: 'center' }}>
      {!!iconSrc && (
        <Box
          sx={{
            display: 'block',
            maxWidth: 100,
            height: 40,
            objectFit: 'contain',
          }}
          component="img"
          src={iconSrc}
          alt={text}
        />
      )}
      {!!faIcon && <Icon icon={faIcon} sx={{ fontSize: 24, color: primaryColor }} />}
      <Typography variant="button">{text}</Typography>
    </Stack>
  )

  if (url)
    return (
      <Button component="a" href={redirectUrl} target="_blank" rel="noreferrer" sx={buttonSx}>
        {buttonInner}
      </Button>
    )

  return (
    <Button sx={buttonSx} onClick={onClick}>
      {buttonInner}
    </Button>
  )
}

const Countdown = ({ countdown, source }: { source: ThirdPartyCollectSource | null; countdown: number }) => {
  const { t } = useI18N()

  if (!source) return <Typography>{t('platform.redirecting')}</Typography>

  return (
    <Typography>
      {t('thirdPartyCollect.autoredirectMessage')
        .toString()
        .replace('{source}', source.source || '')
        .replace('{seconds}', countdown.toString())}
    </Typography>
  )
}

export function PlatformView({
  sources,
  lang,
  sourcesLabel,
  noRedirect,
  hasTextReview,
  hasVideoReview,
  setReviewState,
  copyText,
}: PlatformViewProps) {
  const { isRedirecting, redirectUrl, redirectCountdownSeconds, redirectSource } = usePlatformRedirect(
    sources,
    noRedirect
  )
  const { buttonSx } = useCampaign()
  const { t, tt } = useI18N()
  const { mainSegmentIds } = useSurveyProps()

  const openReviewView = useCallback(() => {
    if (setReviewState) setReviewState('contact info')
  }, [setReviewState])

  if (!sources) return null
  if (isRedirecting) {
    const [source] = sources
    return (
      <Stack gap={2} alignItems="center" py={5}>
        {source.source && (
          <Box
            width={48}
            height={48}
            component="img"
            src={sourceLogos.get(getSourceName(source.source as ReviewConnection['source']))}
          />
        )}
        <Countdown source={redirectSource} countdown={redirectCountdownSeconds} />
        {!!redirectUrl && (
          <Button
            component="a"
            href={redirectUrl}
            variant="contained"
            color="primary"
            target="_blank"
            rel="noreferrer,noopener"
            endIcon={<Icon icon={faExternalLinkAlt} />}
            sx={buttonSx}>
            {t('platform.openLink')}
          </Button>
        )}
      </Stack>
    )
  }

  return (
    <>
      {!!sourcesLabel && (
        <Typography variant="body1" sx={{ textAlign: 'center' }} whiteSpace="pre-wrap">
          {sourcesLabel}
        </Typography>
      )}
      {!!copyText && <CopyText caption={t('thanks.yourReview')} text={copyText.trim()} />}
      <Stack direction="row" gap={2} flexWrap="wrap" justifyContent="center">
        {hasTextReview && (
          <PlatformButton text={t('platform.leaveTextReview')} onClick={openReviewView} faIcon={faPenNib} />
        )}
        {hasVideoReview && !hasTextReview && (
          <PlatformButton text={t('platform.leaveVideoReview')} onClick={openReviewView} faIcon={faVideo} />
        )}
        {sources
          .filter(item => item.id !== 'trustmary')
          .filter(isSourceInMainSegment(mainSegmentIds || []))
          .map(source => (
            <PlatformButton
              key={source.id}
              text={tt(source.name)}
              url={source.url}
              iconSrc={source.source ? sourceLogos.get(getSourceName(source.source as ReviewConnection['source'])) : ''}
            />
          ))}
      </Stack>
    </>
  )
}
